import { type FC } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { useTranslation } from 'react-i18next';
import type { CrisisAlert, AlertLevel } from '@/types';

// ---------------------------------------------------------------------------
// Crisis Alert Banner — shows at top of map for detected war/crisis events
// ---------------------------------------------------------------------------

const LEVEL_STYLES: Record<AlertLevel, { bg: string; border: string; text: string; badge: string; icon: string }> = {
  extreme: {
    bg: 'bg-red-950/95',
    border: 'border-red-500',
    text: 'text-red-100',
    badge: 'bg-red-600 text-white animate-pulse',
    icon: '☢',
  },
  critical: {
    bg: 'bg-orange-950/95',
    border: 'border-orange-500',
    text: 'text-orange-100',
    badge: 'bg-orange-600 text-white',
    icon: '🔴',
  },
  warning: {
    bg: 'bg-yellow-950/90',
    border: 'border-yellow-600',
    text: 'text-yellow-100',
    badge: 'bg-yellow-600 text-black',
    icon: '⚠',
  },
};

interface AlertItemProps {
  alert: CrisisAlert;
  onDismiss: (id: string) => void;
}

const AlertItem: FC<AlertItemProps> = ({ alert, onDismiss }) => {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const style = LEVEL_STYLES[alert.level];
  const title = isKo ? alert.titleKo : alert.title;

  return (
    <div
      className={clsx(
        'flex items-start gap-2 px-3 py-2 border rounded-md backdrop-blur-md mb-1.5',
        style.bg, style.border,
      )}
    >
      {/* Level badge */}
      <span className={clsx('text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5 uppercase tracking-wide', style.badge)}>
        {alert.level}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className={clsx('text-xs font-semibold leading-tight truncate', style.text)}>
          {title}
        </div>
        <div className="text-[10px] text-slate-400 truncate mt-0.5">
          {alert.region} • {alert.confidence}% confidence • {alert.sources.slice(0, 2).join(', ')}
        </div>
      </div>

      {/* Dismiss */}
      <button
        onClick={() => onDismiss(alert.id)}
        className="shrink-0 text-slate-500 hover:text-slate-200 text-xs px-1 transition-colors"
        title="Dismiss"
      >
        ✕
      </button>
    </div>
  );
};

export const CrisisAlertBanner: FC = () => {
  const crisisAlerts = useAppStore((s) => s.crisisAlerts);
  const dismissAlert = useAppStore((s) => s.dismissAlert);

  const activeAlerts = crisisAlerts.filter((a) => !a.dismissed);

  if (!activeAlerts.length) return null;

  // Sort: extreme first, then critical, then warning
  const sorted = [...activeAlerts].sort((a, b) => {
    const order = { extreme: 0, critical: 1, warning: 2 };
    return order[a.level] - order[b.level];
  });

  const topAlerts = sorted.slice(0, 3); // Show max 3 alerts

  return (
    <div className="absolute top-2 left-2 right-auto z-50 w-80 md:w-96 pointer-events-auto">
      <div className="space-y-1">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
          </span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-red-400">
            CRISIS INTEL — {activeAlerts.length} ACTIVE
          </span>
        </div>

        {topAlerts.map((alert) => (
          <AlertItem key={alert.id} alert={alert} onDismiss={dismissAlert} />
        ))}

        {activeAlerts.length > 3 && (
          <div className="text-[10px] text-slate-500 pl-1">
            +{activeAlerts.length - 3} more alerts...
          </div>
        )}
      </div>
    </div>
  );
};
